import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String }

  connect() {
    this.editor = null
    this.dropdown = null
    this.mentionQuery = ""
    this.mentionStartPosition = null
    this.selectedIndex = 0
    this.results = []
    this.debounceTimer = null

    this.trixElement = this.element.querySelector("trix-editor")
    if (!this.trixElement) return

    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleInput = this.handleInput.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)

    // Trix initializes asynchronously — .editor is undefined until trix-initialize fires
    this.trixElement.addEventListener("trix-initialize", () => {
      this.editor = this.trixElement.editor
    })

    this.trixElement.addEventListener("keydown", this.handleKeydown)
    this.trixElement.addEventListener("trix-change", this.handleInput)
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    if (this.trixElement) {
      this.trixElement.removeEventListener("keydown", this.handleKeydown)
      this.trixElement.removeEventListener("trix-change", this.handleInput)
    }
    document.removeEventListener("click", this.handleClickOutside)
    this.closeDropdown()
    clearTimeout(this.debounceTimer)
  }

  handleKeydown(event) {
    // Ctrl+Backspace: override native word-deletion so Trix attachments
    // (mentions) in the range are properly removed
    if (event.key === "Backspace" && (event.ctrlKey || event.metaKey) && this.editor) {
      event.preventDefault()
      const position = this.editor.getPosition()
      if (position === 0) return

      const text = this.editor.getDocument().toString()
      const textBefore = text.substring(0, position)

      // Find previous word boundary (skip trailing spaces, then skip the word)
      const match = textBefore.match(/\S+\s*$/)
      const deleteFrom = match ? position - match[0].length : 0

      this.editor.setSelectedRange([deleteFrom, position])
      this.editor.deleteInDirection("forward")
      return
    }

    if (!this.dropdown) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.results.length - 1)
        this.renderDropdown()
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.renderDropdown()
        break
      case "Enter":
      case "Tab":
        event.preventDefault()
        if (this.results[this.selectedIndex]) {
          this.insertMention(this.results[this.selectedIndex])
        }
        break
      case "Escape":
        event.preventDefault()
        this.closeDropdown()
        break
    }
  }

  handleInput() {
    if (!this.editor) return

    const position = this.editor.getPosition()
    const text = this.editor.getDocument().toString()

    // Look backwards from cursor for an @ that starts a mention
    const textBeforeCursor = text.substring(0, position)
    const atIndex = textBeforeCursor.lastIndexOf("@")

    if (atIndex === -1) {
      this.closeDropdown()
      return
    }

    // Ensure the @ is at the start or preceded by a space/newline
    const charBefore = atIndex > 0 ? textBeforeCursor[atIndex - 1] : " "
    if (charBefore !== " " && charBefore !== "\n" && atIndex !== 0) {
      this.closeDropdown()
      return
    }

    const query = textBeforeCursor.substring(atIndex + 1)

    // Close if there's a newline gap (user moved past the mention)
    if (query.includes("\n")) {
      this.closeDropdown()
      return
    }

    this.mentionQuery = query
    this.mentionStartPosition = atIndex

    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 300)
  }

  async fetchResults(query) {
    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) return

      this.results = await response.json()
      this.selectedIndex = 0

      if (this.results.length > 0) {
        this.showDropdown()
      } else {
        this.closeDropdown()
      }
    } catch {
      this.closeDropdown()
    }
  }

  insertMention(user) {
    // Delete the @query text
    const currentPosition = this.editor.getPosition()
    const range = [this.mentionStartPosition, currentPosition]
    this.editor.setSelectedRange(range)
    this.editor.deleteInDirection("forward")

    // Trix.Attachment preserves the content HTML as-is (class + data-user-id intact).
    // insertHTML would work but Trix sanitizes custom classes/attributes from it.
    const attachment = new Trix.Attachment({
      content: `<span class="mention" data-user-id="${user.id}">@${this.escapeHtml(user.name)}</span>`,
      contentType: 'text/html'
    })
    this.editor.insertAttachment(attachment)
    this.editor.insertString(" ")

    this.closeDropdown()
  }

  showDropdown() {
    if (!this.dropdown) {
      this.dropdown = document.createElement("div")
      this.dropdown.className = "fixed z-50 bg-base-100 border border-base-300 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto w-64"
      // Append to body so the dropdown isn't clipped by any overflow:hidden ancestor
      document.body.appendChild(this.dropdown)
    }

    // Position the dropdown at the cursor using the native selection API
    const selection = window.getSelection()
    if (selection.rangeCount > 0) {
      const rect = selection.getRangeAt(0).getBoundingClientRect()
      this.dropdown.style.top = `${rect.bottom + 4}px`
      this.dropdown.style.left = `${rect.left}px`
    }

    this.renderDropdown()
  }

  renderDropdown() {
    if (!this.dropdown) return

    this.dropdown.innerHTML = this.results.map((user, index) => `
      <button type="button"
              class="w-full text-left px-3 py-2 text-sm hover:bg-base-200 cursor-pointer flex flex-col ${index === this.selectedIndex ? 'bg-base-200' : ''}"
              data-index="${index}">
        <span class="font-medium">${this.escapeHtml(user.name)}</span>
        <span class="text-xs opacity-60">${this.escapeHtml(user.email)}</span>
      </button>
    `).join("")

    this.dropdown.querySelectorAll("button").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault()
        const idx = parseInt(btn.dataset.index)
        this.insertMention(this.results[idx])
      })
    })
  }

  closeDropdown() {
    clearTimeout(this.debounceTimer)
    if (this.dropdown) {
      this.dropdown.remove()
      this.dropdown = null
    }
    this.mentionQuery = ""
    this.mentionStartPosition = null
    this.results = []
    this.selectedIndex = 0
  }

  handleClickOutside(event) {
    if (this.dropdown && !this.element.contains(event.target) && !this.dropdown.contains(event.target)) {
      this.closeDropdown()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
